package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
data class UserDto(
    val id: String,
    val email: String,
    val name: String,
    val avatarColor: String,
    val createdAt: String,
    val updatedAt: String,
    /**
     * Null until the address is confirmed.
     *
     * Nothing is blocked while it is null — an unverified account can still work normally. It gates
     * only what costs money or trust: machine-translation credits, and being reachable by email.
     */
    val emailVerifiedAt: String? = null,
    /**
     * Whether this account has a password at all.
     *
     * **Not a secret, and three refusals depend on it.** Changing your address, handing over a
     * workspace and deleting one all re-prove ownership with a password, and all three refuse an
     * account that signs in with Google and has never set one. Each told the person to "set a
     * password first" — advice they could only follow by signing out and using "Forgot password" on
     * an account whose whole point is that they never chose a password.
     */
    val hasPassword: Boolean = true,
)

@Serializable
data class OrgDto(
    val id: String,
    val name: String,
    val slug: String,
    val createdAt: String,
)

@Serializable
data class MembershipDto(
    val id: String,
    val orgId: String,
    val userId: String,
    val role: Role,
    val createdAt: String,
)

@Serializable
data class AuthResponse(
    val token: String,
    val user: UserDto,
)

@Serializable
data class RegisterRequest(
    val email: String,
    val password: String,
    val name: String,
    val acquisitionSource: String? = null,
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
)

@Serializable
data class ServerFeaturesDto(
    val workspaceScopedOverview: Boolean = false,
    val workspaceScopedActivity: Boolean = false,
    val explicitProjectWorkspace: Boolean = false,
    val explicitSampleWorkspace: Boolean = false,
    /** Distinct guarded save, review, proposal and reaffirmation routes are implemented. */
    val sourceAwareReview: Boolean = false,
    /** Distinct hosted and actual-resource reviewed/current checks are both available. */
    val reviewedCurrentReleaseChecks: Boolean = false,
)

@Serializable
data class MeResponse(
    val user: UserDto,
    /**
     * Every workspace this person belongs to, each carrying the role they hold and what the
     * workspace is paying for.
     *
     * Deliberately not a bare list of names with a second call for roles: the navigation is drawn
     * from this response, and any window in which the role is unknown is a window in which a
     * translator is shown an owner's menu.
     */
    val organizations: List<WorkspaceDto> = emptyList(),
    /** Exact server semantics this response's session generation may rely on. */
    val serverFeatures: ServerFeaturesDto = ServerFeaturesDto(),
)

@Serializable
data class ApiTokenDto(
    val id: String,
    val name: String,
    val prefix: String,
    val createdAt: String,
    val lastUsedAt: String? = null,
    val revokedAt: String? = null,
    /** What this credential may do. Never widened after minting; narrowed by a demotion instantly. */
    val scopes: List<TokenScope> = emptyList(),
    /** The projects it is limited to. Empty means every project its owner can reach. */
    val projectSlugs: List<String> = emptyList(),
    val expiresAt: String? = null,
    /**
     * Minted before scopes existed, and therefore holding all of them until it expires.
     *
     * Surfaced so the dashboard can say *why* a token is expiring rather than presenting a date
     * nobody chose.
     */
    val isLegacy: Boolean = false,
)

@Serializable
data class CreateTokenRequest(
    val name: String,
    /**
     * What the token may do. Empty means [TokenScope.DEFAULT] — read-only.
     *
     * A default that could write is a default that eventually does: the common first token is for a
     * CI job running `lingvo qa`.
     */
    val scopes: List<TokenScope> = emptyList(),
    /** Limit it to these projects. Empty means every project its owner can reach. */
    val projectSlugs: List<String> = emptyList(),
    /** Null means no expiry, which the interface should make people choose rather than assume. */
    val expiresInDays: Int? = null,
)

@Serializable
data class CreatedTokenDto(
    val id: String,
    val name: String,
    val prefix: String,
    /** Shown once and never stored. Only the hash reaches the database. */
    val token: String,
    val scopes: List<TokenScope> = emptyList(),
    val expiresAt: String? = null,
)

@Serializable
data class PasswordResetRequest(
    val email: String,
)

@Serializable
data class PasswordResetConfirmRequest(
    val token: String,
    val password: String,
)

/**
 * Deliberately says nothing about whether the address exists.
 *
 * Answering differently for a known and an unknown address turns the endpoint into a way to test
 * whether someone has an account here.
 */
@Serializable
data class PasswordResetAcceptedDto(
    val message: String = "If that address has an account, a reset link is on its way.",
    /** Present only in development, where mail is not configured — see the server log otherwise. */
    val devToken: String? = null,
)

@Serializable
data class VerifyEmailRequest(
    val token: String,
)

@Serializable
data class ResendVerificationResponse(
    val message: String = "If that address needs confirming, a new link is on its way.",
    /** Development only, where mail is not configured. */
    val devToken: String? = null,
)

/** The single-use code handed to the web app by the Google callback. */
@Serializable
data class OAuthExchangeRequest(val code: String)

/** Which sign-in methods the server actually offers. */
@Serializable
data class AuthProvidersDto(
    val google: Boolean = false,
    /**
     * Paddle's client-side token, or null when this deployment cannot take payments.
     *
     * Public by design — it can open a checkout and nothing else. It is deliberately NOT the API
     * key, which can read customers and cancel subscriptions and must never reach a browser.
     */
    val paddleClientToken: String? = null,
    /** "sandbox" or "production". Paddle.js needs to be told, and it must match the token. */
    val paddleEnvironment: String? = null,
    /**
     * The GitHub App's public slug, or null when this deployment has none configured.
     *
     * Public by design, and not a credential: it is the `<slug>` in
     * `github.com/apps/<slug>/installations/new`, which is the only way anybody can install the app.
     * Without it the import page could explain the permissions it wanted and offer no way to grant
     * them — three paragraphs and no button.
     *
     * Null is meaningful: the page says import is not configured here rather than rendering a link
     * that leads to a 404.
     */
    val githubAppSlug: String? = null,
)

/**
 * One signed-in device.
 *
 * Listed only because revoking one actually works: the `sid` claim is checked against this row on
 * every request. A list without that check shows devices, returns 204 from its revoke button, and
 * leaves the revoked token working for a month.
 *
 * **API tokens are deliberately not listed here.** Mixing them teaches people that revoking a
 * session revokes a token, and the day that matters is the day somebody's laptop is stolen.
 */
@Serializable
data class SessionDto(
    val id: String,
    /** Self-reported by the client, so rendered as escaped text and never as markup. */
    val label: String? = null,
    val createdAt: String,
    val lastSeenAt: String,
    /** Marked rather than hidden, so somebody can see which one they are about to end. */
    val current: Boolean = false,
)
